/**
 Project ENT - Environnement numérique de travail

 @file 	    graph.service.ts
 @author  	Martin Tazlari <martin.tazlari@ictvs.ch>
 @copyright © 2018, ICT-vs. All rights reserved.
 **/

import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { UserInfo } from 'src/app/models/user-info.model';
import { UserListfields } from 'src/app/models/user-list-fields.model';
import {
  ExchangeListFields,
  ResourceListFields,
} from 'src/app/models/resource-list-fields.model';
import { SharemailboxListFields } from 'src/app/models/sharemailbox-list-fields.model';
import { LastUpdateResponse } from 'src/app/models/services/last-update-response.model';
import {
  CollectionUsersResponse,
  GroupsResponse,
  MultiItemResponse,
  SingleItemResponse,
  TeamsResponse,
} from 'src/app/models/services/graph/graph-service-response.model';
import { MaintenanceListFields } from 'src/app/models/maintenance-list-fields.models';
import { DateOnlyResponse } from 'src/app/models/services/date-only-response.model';
import { UserGraph } from 'src/app/models/user-graph.model';
import { UserTeamGraph } from 'src/app/models/user-team-graph.model';
import { GroupGraph } from 'src/app/models/group-graph.model';
import { TeamGraph } from 'src/app/models/team-graph.model';
import { GECorrespondingTable } from 'src/app/models/ge-corresponding-table.model';
import { TeamsListFields } from 'src/app/models/teams-list-fields.model';
import { LocalStorageService } from '../localStorage/localStorage.service';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
  }),
};

export const ExchangeStatus = {
  CREATE: 'Demande de création',
  MODIFY: 'Demande de modification',
  DELETE: 'Demande de suppression',
  UPTODATE: 'A jour',
  ONWAIT: 'En attente',
  ERROR: 'Erreur',
} as const;

export const SharemailboxStatus = {
  DELETE: 'Demande de suppression',
  CREATE: 'Demande de création',
  UPTODATE: 'A jour',
  ERROR: 'Erreur',
  ONWAIT: 'En attente',
  MODIFY: 'Demande de modification',
} as const;

export const ResourceStatus = {
  DELETE: 'Demande de suppression',
  CREATE: 'Demande de création',
  UPTODATE: 'A jour',
  ERROR: 'Erreur',
  ONWAIT: 'En attente',
  MODIFY: 'Demande de modification',
} as const;

export const TeamsStatus = {
  DELETE: 'Demande de suppression',
  CREATE: 'Demande de création',
  UPTODATE: 'A jour',
  ERROR: 'Erreur',
  ONWAIT: 'En attente',
  MODIFY: 'Demande de modification',
} as const;

@Injectable({
  providedIn: 'root',
})
export class GraphService {
  public currentUser: Observable<UserInfo>;
  private maintenanceSettingsCache: Observable<MaintenanceListFields[]> | null =
    null;
  constructor(
    private http: HttpClient,
    private _localStorageService: LocalStorageService
  ) {}

  /**
   *
   * @param fields
   * @returns
   */
  addUserDemand(fields: UserListfields): Observable<Date> {
    return this.http
      .post<DateOnlyResponse>(
        environment.apiUrl + '/graph/list/items/add',
        {
          title: 'Add User List API',
          type: 'User',
          fields: fields,
        },
        httpOptions
      )
      .pipe(
        map(response => {
          return response.currentDate;
        })
      );
  }

  /**
   *
   * @param sharemailbox
   * @returns
   */
  addSharemailbox(sharemailbox: SharemailboxListFields): Observable<Date> {
    return this.http
      .post<DateOnlyResponse>(
        environment.apiUrl + '/graph/list/items/add',
        {
          title: 'Add SharemailBox List API',
          type: 'Sharemailbox',
          fields: sharemailbox,
        },
        httpOptions
      )
      .pipe(
        map(response => {
          return response.currentDate;
        })
      );
  }

  /**
   *
   * @param ressource
   * @returns
   */
  addRessource(ressource: ResourceListFields): Observable<Date> {
    return this.http
      .post<DateOnlyResponse>(
        environment.apiUrl + '/graph/list/items/add',
        {
          title: 'Add Ressource List API',
          type: 'Ressource',
          fields: ressource,
        },
        httpOptions
      )
      .pipe(
        map(response => {
          return response.currentDate;
        })
      );
  }

  /**
   *
   * @param teams
   * @returns
   */
  addTeams(teams: TeamsListFields, type: string): Observable<Date> {
    return this.http
      .post<DateOnlyResponse>(
        environment.apiUrl + '/graph/list/items/add',
        {
          title: 'Add Teams List API',
          type: type,
          fields: teams,
        },
        httpOptions
      )
      .pipe(
        map(response => {
          return response.currentDate;
        })
      );
  }

  /**
   *
   * @returns
   */
  getListMaintenanceMessage(): Observable<MaintenanceListFields[]> {
    const cachedData =
      this._localStorageService.getItem<MaintenanceListFields[]>(
        'graphMessages'
      ); // Check if cached

    if (cachedData && Array.isArray(cachedData)) {
      return of(cachedData);
    }

    // If not cached
    if (!this.maintenanceSettingsCache) {
      this.maintenanceSettingsCache = this.http
        .get<{
          items: MaintenanceListFields[];
        }>(environment.apiUrl + '/graph/list/maintenance')
        .pipe(
          tap(response => {
            if (!response || !Array.isArray(response.items)) {
              console.error(
                'Invalid API response, expected an array inside `items`:',
                response
              );
              response = { items: [] };
            }
            this._localStorageService.setItem('graphMessages', response.items);
          }),
          map(response => response.items || []),
          catchError(() => {
            return of([]);
          }),
          shareReplay(1) // Avoids duplicate HTTP requests- same underlying observable is shared between multiple subscribers
        );
    }

    return this.maintenanceSettingsCache;
  }

  /**
   *
   * @returns
   */
  getListUserDemands(): Observable<UserListfields[]> {
    return this.http
      .get<MultiItemResponse>(environment.apiUrl + '/graph/list/user/empty')
      .pipe(
        map(response => {
          return response.items as UserListfields[];
        })
      );
  }

  /**
   *
   * @param searchGE
   * @returns
   */
  getListResources(searchGE: string): Observable<ExchangeListFields[]> {
    return this.http
      .get<MultiItemResponse>(
        environment.apiUrl + `/graph/list/ressource/${searchGE}`
      )
      .pipe(
        map(response => {
          const resources = response.items as ResourceListFields[];
          return resources.map(item => {
            return {
              displayName: item.title,
              state: item.etat_Ressource,
              type: item.typedelaressource,
              entAccesDelete: item.entAccesDelete,
              entAccessEdit: item.entAccessEdit,
              id: item.id,
            } as ExchangeListFields;
          });
        })
      );
  }

  /**
   *
   * @param searchGE
   * @returns
   */
  getListSharemailbox(searchGE: string): Observable<ExchangeListFields[]> {
    return this.http
      .get<MultiItemResponse>(
        environment.apiUrl + `/graph/list/sharemailbox/${searchGE}`
      )
      .pipe(
        map(response => {
          const sharedmailboxes = response.items as SharemailboxListFields[];
          return sharedmailboxes.map(item => {
            return {
              displayName: item.title,
              state: item.etatdelabo_x00ee_te,
              email: item.mailBoxAddress,
              entAccesDelete: item.entAccesDelete,
              entAccessEdit: item.entAccessEdit,
              id: item.id,
            } as ExchangeListFields;
          });
        })
      );
  }

  /**
   *
   * @param searchGE
   * @returns
   */
  getListTeams(searchGE: string, teamsType: string, schoolYear: string, nameFilter = ""): Observable<TeamsListFields[]> {
    let apiUrl = environment.apiUrl + `/graph/list/${teamsType}-teams/${searchGE}`;
    if (schoolYear) {
      apiUrl += `/${schoolYear}`;
    }

    if( nameFilter && nameFilter.length > 0) {
      apiUrl += `?nameFilter=${nameFilter}`;
    }
    
    return this.http
      .get<MultiItemResponse>(
        apiUrl
      )
      .pipe(
        map(response => {
          const teams = response.items as TeamsListFields[];
          return teams.map((item) => {
            return {
              displayName: item.title,
              state: item.rqEtat,
              email: item.rqugPrimarySmtpAddress,
              id: item.rqteGuid,
              schoolYear: item.coSchoolYear,
            } as ExchangeListFields;
          });
        })
      );
  }

  /**
   *
   * @param id
   * @returns
   */
  getRessourceById(id: string): Observable<ResourceListFields> {
    return this.http
      .get<SingleItemResponse>(environment.apiUrl + `/graph/ressource/${id}`)
      .pipe(
        map(response => {
          return response.item as ResourceListFields;
        })
      );
  }

  /**
   *
   * @param id
   * @returns
   */
  getSharemailboxById(id: string): Observable<SharemailboxListFields> {
    return this.http
      .get<SingleItemResponse>(environment.apiUrl + `/graph/sharemailbox/${id}`)
      .pipe(
        map(response => {
          return response.item as SharemailboxListFields;
        })
      );
  }

  /**
   *
   * @param ressourceID
   * @returns
   */
  deleteRessource(ressourceID: string): Observable<Date> {
    return this.http
      .patch<DateOnlyResponse>(
        environment.apiUrl + '/graph/list/items/update',
        {
          type: 'Ressource',
          id: ressourceID,
          fields: {
            type: 'Ressource',
            etat_Ressource: ResourceStatus.DELETE,
          },
        },
        httpOptions
      )
      .pipe(
        map(response => {
          return response.currentDate;
        })
      );
  }

  /**
   *
   * @param sharemailboxID
   * @returns
   */
  deleteSharemailbox(sharemailboxID: string): Observable<Date> {
    return this.http
      .patch<DateOnlyResponse>(
        environment.apiUrl + '/graph/list/items/update',
        {
          type: 'Sharemailbox',
          id: sharemailboxID,
          fields: {
            type: 'Sharemailbox',
            etatdelabo_x00ee_te: SharemailboxStatus.DELETE,
          },
        },
        httpOptions
      )
      .pipe(
        map(response => {
          return response.currentDate;
        })
      );
  }

  /**
   *
   * @param ressourceID
   * @param updateRessource
   * @returns
   */
  updateRessource(
    ressourceID: string,
    updateRessource: ResourceListFields
  ): Observable<Date> {
    return this.http
      .patch<DateOnlyResponse>(
        environment.apiUrl + '/graph/list/items/update',
        {
          type: 'Ressource',
          id: ressourceID,
          fields: {
            type: updateRessource.type,
            capacit_x00e9_: updateRessource.capacit_x00e9_,
            displayInGAL: updateRessource.displayInGAL,
            title: updateRessource.title,
            rue: updateRessource.rue,
            etat_Ressource: updateRessource.etat_Ressource,
            rqarbipGrDirecteur: updateRessource.rqarbipGrDirecteur,
            rqarbipGrConseilDirection:
              updateRessource.rqarbipGrConseilDirection,
            rqarbipGrSecretaireDirection:
              updateRessource.rqarbipGrSecretaireDirection,
            rqarbipGrAdministratif: updateRessource.rqarbipGrAdministratif,
            rqarbipGrTechnique: updateRessource.rqarbipGrTechnique,
            rqarbipGrITResponsable: updateRessource.rqarbipGrITResponsable,
            rqarbipGrITGestionnaire: updateRessource.rqarbipGrITGestionnaire,
            rqarbipGrPersonnelEnseignant:
              updateRessource.rqarbipGrPersonnelEnseignant,
            rqarFullAccessGrDirecteur:
              updateRessource.rqarFullAccessGrDirecteur,
            rqarFullAccessGrConseilDirection:
              updateRessource.rqarFullAccessGrConseilDirection,
            rqarFullAccessGrSecretaireDirect:
              updateRessource.rqarFullAccessGrSecretaireDirect,
            rqarFullAccessGrAdministratif:
              updateRessource.rqarFullAccessGrAdministratif,
            rqarFullAccessGrTechnique:
              updateRessource.rqarFullAccessGrTechnique,
            rqarFullAccessGrITResponsable:
              updateRessource.rqarFullAccessGrITResponsable,
            rqarFullAccessGrITGestionnaire:
              updateRessource.rqarFullAccessGrITGestionnaire,
          },
        },
        httpOptions
      )
      .pipe(
        map(response => {
          return response.currentDate;
        })
      );
  }

  /**
   *
   * @param ressourceID
   * @param updateSharemailbox
   * @returns
   */
  updateSharemailbox(
    ressourceID: string,
    updateSharemailbox: SharemailboxListFields
  ): Observable<Date> {
    return this.http
      .patch<DateOnlyResponse>(
        environment.apiUrl + '/graph/list/items/update',
        {
          type: 'Sharemailbox',
          id: ressourceID,
          fields: {
            type: updateSharemailbox.type,
            displayInGAL: updateSharemailbox.displayInGAL,
            title: updateSharemailbox.title,
            etatdelabo_x00ee_te: updateSharemailbox.etatdelabo_x00ee_te,
            rqarFullAccessGrDirecteur:
              updateSharemailbox.rqarFullAccessGrDirecteur,
            rqarFullAccessGrConseilDirection:
              updateSharemailbox.rqarRFullAccessGrConseilDirection,
            rqarFullAccessGrSecretaireDirect:
              updateSharemailbox.rqarFullAccessGrSecretaireDirect,
            rqarFullAccessGrAdministratif:
              updateSharemailbox.rqarFullAccessGrAdministratif,
            rqarFullAccessGrTechnique:
              updateSharemailbox.rqarFullAccessGrTechnique,
            rqarFullAccessGrITResponsable:
              updateSharemailbox.rqarFullAccessGrITResponsable,
            rqarFullAccessGrITGestionnaire:
              updateSharemailbox.rqarFullAccessGrITGestionnaire,
            rqarSendAsGrAdministratif:
              updateSharemailbox.rqarSendAsGrAdministratif,
            rqarSendAsGrConseilDirection:
              updateSharemailbox.rqarSendAsGrConseilDirection,
            rqarSendAsGrDirecteur: updateSharemailbox.rqarSendAsGrDirecteur,
            rqarSendAsGrITGestionnaire:
              updateSharemailbox.rqarSendAsGrITGestionnaire,
            rqarSendAsGrITResponsable:
              updateSharemailbox.rqarSendAsGrITResponsable,
            rqarSendAsGrSecretaireDirection:
              updateSharemailbox.rqarSendAsGrSecretaireDirection,
            rqarSendAsGrTechnique: updateSharemailbox.rqarSendAsGrTechnique,
            rqarFullAccessUsersOrGroups:
              updateSharemailbox.rqarFullAccessUsersOrGroups,
            rqaSendAsUsersOrGroups: updateSharemailbox.rqaSendAsUsersOrGroups,
          },
        },
        httpOptions
      )
      .pipe(
        map(response => {
          return response.currentDate;
        })
      );
  }

  /**
   * Force the API to load or upate the GE cache
   * @returns An observable with an empty response
   */
  fetchCorrespondingTable(): Observable<void> {
    return this.http.get<void>(
      environment.apiUrl + '/graph/list/correspondingTable/populate',
      {}
    );
  }

  /**
   * Get the cache last updated datetime as a string
   * @returns
   */
  getLastUpdateDate(): Observable<string> {
    return this.http
      .get<LastUpdateResponse>(
        environment.apiUrl + '/graph/list/correspondingTable/lastupdate',
        {}
      )
      .pipe(
        map(response => {
          return response.lastupdate;
        })
      );
  }

  /**
   *
   * @param sharemailbox
   * @returns
   */
  selectGeInCorrespondingTable(
    ge: GECorrespondingTable
  ): Observable<GECorrespondingTable> {
    return this.http
      .post<any>(
        environment.apiUrl + '/graph/list/correspondingTable',
        ge,
        httpOptions
      )
      .pipe(
        map(response => {
          return response.ge as GECorrespondingTable;
        })
      );
  }

  /**
   *
   * @param userEmail
   * @returns
   */
  canUserBeAccepted(userEmail: string): Observable<boolean> {
    return this.http
      .get<boolean>(
        environment.apiUrl + '/graph/canUserBeAccepted?userEmail=' + userEmail,
        httpOptions
      )
      .pipe(
        map(response => {
          return response;
        })
      );
  }

  /**
   *
   * @param userEmail
   * @returns
   */
  getMicrosoftUserInfo(userEmail: string): Observable<UserGraph> {
    return this.http
      .get<SingleItemResponse>(
        environment.apiUrl + '/graph/infos/user?userEmail=' + userEmail,
        httpOptions
      )
      .pipe(
        map(response => {
          return response.user;
        })
      );
  }

  /**
   *
   * @param userEmail
   * @returns
   */
  getUserSharedMailboxes(userEmail: string): Observable<string[]> {
    return this.http
      .get<SingleItemResponse>(
        environment.apiUrl + '/graph/user/exchange/sharedmailboxes?userEmail=' + userEmail,
        httpOptions
      )
      .pipe(
        map(response => {
          return response.sharedmailboxes;
        })
      );
  }

  /**
   *
   * @param userId
   * @returns
   */
  getMicrosoftUserTeamsInfo(userId: string): Observable<UserTeamGraph[]> {
    return this.http
      .get<MultiItemResponse>(
        environment.apiUrl + '/graph/infos/user/teams?userId=' + userId,
        httpOptions
      )
      .pipe(
        map(response => {
          return response.userTeams as UserTeamGraph[];
        })
      );
  }

  /**
   *
   * @param userId
   * @returns
   */
  getMicrosoftUserGroupsInfo(userId: string): Observable<GroupGraph[]> {
    return this.http
      .get<MultiItemResponse>(
        environment.apiUrl + '/graph/infos/user/groups?userId=' + userId,
        httpOptions
      )
      .pipe(
        map(response => {
          return response.groups as GroupGraph[];
        })
      );
  }

  getEntityInfo(
    entityId: string,
    entityType: string
  ): Observable<GroupGraph | TeamGraph> {
    if (entityType === 'team') {
      return this.getSpecificMicrosoftTeam(entityId);
    } else if (entityType === 'group') {
      return this.getSpecificMicrosoftGroup(entityId);
    }
  }

  private getSpecificMicrosoftGroup(groupId: string): Observable<GroupGraph> {
    return this.http
      .get<SingleItemResponse>(
        environment.apiUrl + '/graph/groups/' + groupId,
        httpOptions
      )
      .pipe(
        map(response => {
          return response.group;
        })
      );
  }

  private getSpecificMicrosoftTeam(teamId: string): Observable<TeamGraph> {
    return this.http
      .get<SingleItemResponse>(
        environment.apiUrl + '/graph/teams/' + teamId,
        httpOptions
      )
      .pipe(
        map(response => {
          return response.team;
        })
      );
  }

  /**
   * @returns
   */
  restoreDeletedTeam(teamId: string): Observable<TeamsResponse> {
    return this.http
      .post<TeamsResponse>(environment.apiUrl + '/graph/teams/restore/'+teamId, httpOptions)
      .pipe(
        map(response => {
          return response;
        })
      );
  }

  /**
   * @returns
   */
  getDeletedTeamsPage(nextPageLink?: string): Observable<TeamsResponse> {
    const params = nextPageLink
      ? new HttpParams().set('nextPageLink', nextPageLink)
      : undefined;

    return this.http
      .get<TeamsResponse>(environment.apiUrl + '/graph/teams/deleted', { params })
      .pipe(
        map(response => {
          return response;
        })
      );
  }

  /**
   * @returns
   */
  getFilteredDeletedTeams(
    displayName: string,
    mail: string,
    startDate: Date,
    endDate: Date,
  ): Observable<TeamsResponse> {
    let params = new HttpParams();
    if (displayName!= "") params = params.append('displayName', displayName);
    if (mail!= "") params = params.append('mail', mail);
    if (startDate != null && endDate != null) {
      params = params.append('startDate', startDate.toISOString());
      params = params.append('endDate', endDate.toISOString());
    }

    return this.http
      .get<TeamsResponse>(environment.apiUrl + '/graph/teams/deleted/filtered', {
        params,
      })
      .pipe(
        map(response => {
          return response;
        })
      );
  }

  /**
   * @returns
   */
  getGroupsPage(nextPageLink?: string): Observable<GroupsResponse> {
    const params = nextPageLink
      ? new HttpParams().set('nextPageLink', nextPageLink)
      : undefined;

    return this.http
      .get<GroupsResponse>(environment.apiUrl + '/graph/groups', { params })
      .pipe(
        map(response => {
          return response;
        })
      );
  }

  /**
   * @returns
   */
  getFilteredGroups(
    displayName: string,
    description: string
  ): Observable<GroupsResponse> {
    let params = new HttpParams();
    params = params.append('displayName', displayName);
    params = params.append('description', description);

    return this.http
      .get<GroupsResponse>(environment.apiUrl + '/graph/groups/filtered', {
        params,
      })
      .pipe(
        map(response => {
          return response;
        })
      );
  }

  getCollectionUsers(
    entityId: string,
    userType: string,
    nextPageLink?: string
  ): Observable<CollectionUsersResponse> {
    const params = nextPageLink
      ? new HttpParams().set('nextPageLink', nextPageLink)
      : undefined;
    return this.http
      .get<CollectionUsersResponse>(
        environment.apiUrl + `/graph/${entityId}/${userType}`,
        { params }
      )
      .pipe(
        map(response => {
          return response;
        })
      );
  }

  getFilteredCollectionUsers(
    entityId: string,
    userType: string,
    firstName: string,
    lastName: string,
    email: string
  ): Observable<CollectionUsersResponse> {
    let params = new HttpParams();
    params = params.append('firstName', firstName);
    params = params.append('lastName', lastName);
    params = params.append('email', email);

    return this.http
      .get<CollectionUsersResponse>(
        environment.apiUrl + `/graph/${entityId}/${userType}/filtered`,
        { params }
      )
      .pipe(
        map(response => {
          return response;
        })
      );
  }
}
